import { useTranslation } from 'react-i18next';

export interface FilterConfig {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

interface FilterBarProps {
  filters: FilterConfig[];
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

export default function FilterBar({
  filters,
  activeFilters,
  onFilterChange,
  onClear,
}: FilterBarProps) {
  const { t } = useTranslation();

  const hasActive = Object.values(activeFilters).some((v) => v !== '');

  return (
    <div className="space-y-2">
      {/* Dropdowns row */}
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((filter) => (
          <select
            key={filter.key}
            value={activeFilters[filter.key] ?? ''}
            onChange={(e) => onFilterChange(filter.key, e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            <option value="">{filter.label}</option>
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ))}

        {hasActive && (
          <button
            onClick={onClear}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {hasActive && (
        <div className="flex flex-wrap items-center gap-1.5">
          {filters.map((filter) => {
            const val = activeFilters[filter.key];
            if (!val) return null;
            const opt = filter.options.find((o) => o.value === val);
            return (
              <span
                key={filter.key}
                className="inline-flex items-center gap-1 rounded-full bg-primary-100 text-primary-700 px-2.5 py-0.5 text-xs font-medium"
              >
                {filter.label}: {opt?.label ?? val}
                <button
                  onClick={() => onFilterChange(filter.key, '')}
                  className="hover:text-primary-900 transition-colors"
                  aria-label={`Remove ${filter.label} filter`}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
